import moment from "moment";

// SQL SUM() over zero rows comes back NULL, which the DataSet→JSON layer can
// serialize as {} rather than null — coerce through Number() so an empty
// aggregate reads as "no value" instead of leaking an object into the UI.
export const numOf = (v) => {
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
};

export const topItem = (list, labelKey, valueKey) => {
  if (!list?.length) return null;
  const sorted = [...list].sort(
    (a, b) => numOf(b[valueKey]) - numOf(a[valueKey])
  );
  const top = sorted[0];
  const topValue = top && numOf(top[valueKey]);
  if (!top || !topValue) return null;
  const total = list.reduce((sum, item) => sum + numOf(item[valueKey]), 0);
  const percentage = total > 0 ? ((topValue / total) * 100).toFixed(2) : 0;
  return { label: top[labelKey], value: topValue, percentage };
};

export const sumValues = (list, valueKey) =>
  (list || []).reduce((sum, item) => sum + numOf(item[valueKey]), 0);

export const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : n);

// Naive "+s" pluralization ("category" -> "categorys") is wrong for any
// dimension label ending in a consonant + y - English pluralizes those as
// "y" -> "ies" ("category" -> "categories"). Mirrors the backend's own
// Pluralize() helper (ExportActivityPdfModelBuilder.cs) exactly, so the web
// and the PDF never disagree (Task: "Top categorys"/"unique categorys
// searched" spelling - Problem 31, 2026-09-22).
export const pluralize = (word) => {
  if (!word) return word;
  const last = word[word.length - 1]?.toLowerCase();
  const beforeLast = word.length > 1 ? word[word.length - 2]?.toLowerCase() : "";
  if (last === "y" && beforeLast && !"aeiou".includes(beforeLast)) {
    return word.slice(0, -1) + "ies";
  }
  return word + "s";
};

// Delta arrows come from backend Task 1613 (DiamondSummaryKPIs). Absent that
// table (older API, or a metric it doesn't cover), cards render the current
// value only — never a faked trend.
export const trendFor = (summaryKPIs, metric) => {
  const row = (summaryKPIs || []).find((r) => r.Metric === metric);
  if (!row || !row.PriorValue) return null;
  const change = ((row.CurrentValue - row.PriorValue) / row.PriorValue) * 100;
  return { change, up: change >= 0 };
};

// Mirrors the backend's prior-period window (Task 1613): same length as the
// selected range, ending the day before it starts.
export const priorPeriodLabel = (dataRangeFrom, dataRangeTo) => {
  if (!dataRangeFrom || !dataRangeTo) return "";
  const from = moment(dataRangeFrom);
  const to = moment(dataRangeTo);
  const durationDays = to.diff(from, "days");
  const priorTo = from.clone().subtract(1, "days");
  const priorFrom = priorTo.clone().subtract(durationDays, "days");
  const sameYear = priorFrom.year() === priorTo.year();
  const fromLabel = priorFrom.format(sameYear ? "MMM D" : "MMM D, YYYY");
  const toLabel = priorTo.format("MMM D, YYYY");
  return `${fromLabel} – ${toLabel}`;
};

// Same dates one year earlier - the Global reports' prior window (Task 2345).
export const lastYearLabel = (dataRangeFrom, dataRangeTo) => {
  if (!dataRangeFrom || !dataRangeTo) return "";
  const from = moment(dataRangeFrom).subtract(1, "year");
  const to = moment(dataRangeTo).subtract(1, "year");
  return `${from.format("MMM D, YYYY")} – ${to.format("MMM D, YYYY")}`;
};

// Percent-only "Total" card (Task 2344) on pages whose total IS the whole
// table (Category, Chart, Add to Cart, Wish List) - its share would always be
// 100%, so the card shows the change vs the prior period instead:
// (now - before) / before x 100, with the compared dates underneath.
// Always a number: no data before but some now = ▲ 100% (new), none in
// either = 0.00%. prior = null while the prior total is still loading.
export const pctTotalKpi = (current, prior, dataRangeFrom, dataRangeTo, t, priorLastYear = true) => {
  const priorRange = priorLastYear ? lastYearLabel(dataRangeFrom, dataRangeTo) : priorPeriodLabel(dataRangeFrom, dataRangeTo);
  const now = numOf(current);
  if (prior == null) return { val: "…", valClass: "", sub: `${t("vs")} ${priorRange}` };
  const before = numOf(prior);
  if (before <= 0) {
    return now > 0
      ? { val: "▲ 100.00%", valClass: "edr-up", sub: `${t("new vs")} ${priorRange}` }
      : { val: "0.00%", valClass: "", sub: `${t("vs")} ${priorRange}` };
  }
  const change = ((now - before) / before) * 100;
  return {
    val: `${change >= 0 ? "▲" : "▼"} ${Math.abs(change).toFixed(2)}%`,
    valClass: change >= 0 ? "edr-up" : "edr-down",
    sub: `${t("vs")} ${priorRange}`,
  };
};

// Percent-only "Total Clicks" card (Task 2344) on single-attribute pages
// (Metal Type, Price, Diamond Shape/Size/...): this attribute's clicks as a
// share of ALL clicks in the table for the same dates, e.g. 174 metal-type
// clicks of 427 jewelry clicks = 40.75%. allClicks = null while loading.
export const pctShareKpi = (partClicks, allClicks, shareLabel, t) => {
  if (allClicks == null) return { val: "…", valClass: "", sub: t(shareLabel) };
  const all = numOf(allClicks);
  return { val: `${all > 0 ? ((numOf(partClicks) / all) * 100).toFixed(2) : "0.00"}%`, valClass: "", sub: t(shareLabel) };
};
